import { OptionHelpers } from '../../helpers/option-helpers';
import { IChartContainer } from '../../models/base/chart-container.interface';
import { IChartDataService } from '../../models/base/chart-data-service.interface';
import { ChartStyleSettingsModel } from '../../models/chart-style/chart-style-settings.model';
import { ChartTypeOptionModel } from '../../models/drop-down-list/chart-type-option.model';
import { ChartType } from '../../models/enum/chart-type.enum';
import { TiChartMenuContainer } from "../../ti-chart-menu.container";
import { IComponent } from "../component.interface";

export class ChartTypeComponent implements IComponent {
    public id = '';

    private chartTypeElement: HTMLElement;
    private chartService: IChartDataService;
    public chartTypeOptions = OptionHelpers.getChartTypeOptions();
    public currentChartType: ChartTypeOptionModel = this.chartTypeOptions[0]!;
    
    private tiChartMenuContainer: TiChartMenuContainer;
    private options: HTMLElement;
    private base: IChartContainer;

    constructor(chartTypeElement: HTMLElement, base: IChartContainer, tiChartMenuContainer: TiChartMenuContainer) {
        this.chartTypeElement = chartTypeElement;
        this.base = base;
        this.options = this.chartTypeElement.findFirst('ul');
        this.chartService = base.chartService;
        this.tiChartMenuContainer = tiChartMenuContainer;
        this.subscribeChartStyleData();
        this.onChartTypeClick();
        this.onScroll();
    }

    public show() {
        this.chartTypeElement.classList.add('active');
    }

    public hide() {
        this.chartTypeElement.classList.remove('active');
        this.hideOptions();
    }

    private updateChartStyleDataTimer: any;
    private subscribeChartStyleData() {
        this.chartService.subscribeChartStyleData((chartStyleSettings: ChartStyleSettingsModel) => {
            clearTimeout(this.updateChartStyleDataTimer);
            setTimeout(() => {
                this.selectChartTypeOption(this.options.findFirst('.tf[data-value="' + chartStyleSettings.chartType + '"]'));
                this.changeChartType(chartStyleSettings.chartType);
            }, 300);
        });
    }

    private changeChartType(type: ChartType) {
        const option = this.chartTypeOptions.find((x: ChartTypeOptionModel) => x.type == type);
        if (!option || this.currentChartType.type == type) {
            return;
        }

        this.currentChartType = option;
    }

    private selectChartTypeOption(op: HTMLElement | null) {
        if (!op) { return; }
        const allOptions = this.options.querySelectorAll<HTMLElement>('.tf');
        this.removeActiveElements(allOptions);
        op.classList.add('active');
        const value = parseInt(op.dataset.value, 10);
        const option = this.chartTypeOptions.find((x: ChartTypeOptionModel) => x.type == value);

        if (this.currentChartType && option && this.currentChartType.type != option.type) {
            this.setCurrentOptionImg(option);
            this.setChartType(option);
        }
    }

    private setCurrentOptionImg(option: ChartTypeOptionModel) {
        if (!option) {
            return;
        }

        const lightImg = this.chartTypeElement.findFirst('.current-chart-type.light-img');
        const darkImg = this.chartTypeElement.findFirst('.current-chart-type.dark-img');
        if (!lightImg || !darkImg) {
            return;
        }

        if (option.type == ChartType.Candlestick) {
            lightImg.setAttribute('src', `${this.base.basePath}assetfiles/img/menu/chartstyle.png`);
            darkImg.setAttribute('src', `${this.base.basePath}assetfiles/img/menu/chartstyle-drk.png`);
        } else if (option.type == ChartType.Line) {
            lightImg.setAttribute('src', `${this.base.basePath}assetfiles/img/chart-line-black.svg`);
            darkImg.setAttribute('src', `${this.base.basePath}assetfiles/img/chart-line.svg`);
        } else if (option.type == ChartType.Bar) {
            lightImg.setAttribute('src', `${this.base.basePath}assetfiles/img/bar-black.svg`);
            darkImg.setAttribute('src', `${this.base.basePath}assetfiles/img/bar.svg`);
        } else if (option.type == ChartType.Area) {
            lightImg.setAttribute('src', `${this.base.basePath}assetfiles/img/area-black.svg`);
            darkImg.setAttribute('src', `${this.base.basePath}assetfiles/img/area.svg`);
        } else {
            lightImg.setAttribute('src', `${this.base.basePath}assetfiles/img/ohlc-black.svg`);
            darkImg.setAttribute('src', `${this.base.basePath}assetfiles/img/ohlc.svg`);
        }
    }

    private openChartTypesMenu() {
        this.toggleChartType(!this.tiChartMenuContainer.displayChartType);

        if (this.options) {
            const rect = this.chartTypeElement.getBoundingClientRect();
            this.options.style.top = (rect.y + 20) + 'px';
            this.options.style.left = (rect.x - 4) + 'px';

            if (this.tiChartMenuContainer.displayChartType) {
                this.options.classList.add('active');
            } else {
                this.options.classList.remove('active');
            }
        }
    }

    private onChartTypeClick() {
        this.chartTypeElement.addEventListener('click', (clickEvent: any) => {
            clickEvent.stopPropagation();
            this.openChartTypesMenu();
        });

        const allOptions = this.options.querySelectorAll<HTMLElement>('.tf');
        allOptions.forEach((option: HTMLElement) => {
            option.addEventListener('click', (clickEvent: any) => {
                clickEvent.stopPropagation();
                if (this.base.tiChartSeries?.chartStyleSettings?.isComparisonSymbolPercentageMode) { return; }
                this.selectChartTypeOption(option);

                this.tiChartMenuContainer.closeAllMenuControls();
            });
        });

        this.chartTypeElement.removeChild(this.options);
        this.base.chartWrapper.insertAdjacentElement('beforeend', this.options);
    }

    private toggleChartType(active: boolean) {
        this.tiChartMenuContainer.displayChartType = active;

        this.tiChartMenuContainer.displayTimeframe = false;
        this.tiChartMenuContainer.displayIndicator = false;
        this.tiChartMenuContainer.displayTools = false;
        this.tiChartMenuContainer.displayTradeOrderType = false;
        this.base.hideTooltip();
        this.tiChartMenuContainer.toggleControls();

    }

    private removeActiveElements(options: NodeListOf<HTMLElement>) {
        options?.forEach((option: HTMLElement) => {
            option.classList.remove('active');
        });
    }

    private setChartType(option: ChartTypeOptionModel) {
        if (!this.base.tiChartSeries || this.currentChartType.type === option.type) {
            return;
        }

        this.base.tiChartSeries.ready = false;
        this.chartService.requestNewChartType(option.type);
        this.changeChartType(option.type);
    }
    
    private onScroll() {
        this.base.domEvents.onScroll(this.id, () => {
            this.hideOptions();
        });
    }

    private hideOptions() {
        this.chartTypeElement.removeClass('active');
        this.options?.removeClass('active');

        this.tiChartMenuContainer.displayChartType = false;
    }
}
